import { queryOptions } from "@tanstack/react-query";
import type { OccCartUserPath } from "~/app/api/occ/occUserPath";
import { consumeGuestCheckoutContextFragment, GUEST_CHECKOUT_CONTEXT_HEADER } from "./guestCheckoutContextTransport";
import { fetchOccResourcePageData } from "./pageDataClient";
import { pageDataKeys } from "./pageDataKeys";
import type { PageInitialDataResult } from "./pageInitialDataTypes";
import type { CartRequestPageInitialData } from "./cartRequestPageInitialData";

export const cartRequestPageQueryOptions = (userPath: OccCartUserPath, activeCartId?: string) => {
  const context = consumeGuestCheckoutContextFragment();
  return queryOptions({
    queryKey: pageDataKeys.cartRequest(userPath, activeCartId),
    queryFn: () =>
      fetchOccResourcePageData<PageInitialDataResult<CartRequestPageInitialData>>(
        `${userPath}/carts/${encodeURIComponent(requireActiveCartId(activeCartId))}/page-data/cart/request`,
        {
          headers: context ? { [GUEST_CHECKOUT_CONTEXT_HEADER]: context } : undefined,
        },
      ),
    enabled: Boolean(activeCartId),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

export const cartRequestMergeTransitionQueryOptions = () => {
  const context = consumeGuestCheckoutContextFragment();
  return queryOptions({
    queryKey: [...pageDataKeys.cartRequest("/users/current"), "merge-transition"],
    queryFn: () =>
      fetchOccResourcePageData<PageInitialDataResult<void>>("/schaeffler/page-data/cart/request/merge-transition", {
        headers: context ? { [GUEST_CHECKOUT_CONTEXT_HEADER]: context } : undefined,
      }),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

/** Uses the server-owned cart-request route when the browser has no Active Cart resource identity to scope. */
export const cartRequestServerContextQueryOptions = () => {
  const context = consumeGuestCheckoutContextFragment();
  return queryOptions({
    queryKey: [...pageDataKeys.cartRequest("/users/anonymous"), "server-context"],
    queryFn: () =>
      fetchOccResourcePageData<PageInitialDataResult<CartRequestPageInitialData>>(
        "/schaeffler/page-data/cart/request",
        { headers: context ? { [GUEST_CHECKOUT_CONTEXT_HEADER]: context } : undefined },
      ),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

function requireActiveCartId(activeCartId: string | undefined): string {
  if (!activeCartId) {
    throw new Error("Cannot load Cart Request page data without an Active Cart ID");
  }
  return activeCartId;
}
